import React, { useState, useEffect } from 'react';
import { 
  BellIcon,
  EnvelopeIcon,
  PhoneIcon,
  DevicePhoneMobileIcon,
  PlusIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import axiosInstance from '../../utils/axios';

// Simple Switch component to replace @headlessui/react Switch
const Switch = ({ checked, onChange, className, children }) => {
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div className={`${className} ${
        checked ? 'bg-blue-600' : 'bg-gray-200'
      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors peer-focus:ring-2 peer-focus:ring-blue-500`}>
        <span
          className={`${
            checked ? 'translate-x-6' : 'translate-x-1'
          } inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform`}
        />
      </div>
      {children}
    </label>
  );
};

const NotificationPreferencesEditor = ({ userId, preferences: initialPreferences, onUpdate }) => {
  const [preferences, setPreferences] = useState(initialPreferences || {});
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');

  useEffect(() => {
    setPreferences(initialPreferences || {});
  }, [initialPreferences]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const response = await axiosInstance.put(`/notifications/preferences/${userId}`, {
        preferences
      });
      
      if (response.data.success) {
        setIsEditing(false);
        if (onUpdate) {
          onUpdate(response.data.preferences);
        }
      }
    } catch (err) {
      console.error('Error saving notification preferences:', err);
      setError('Failed to save notification preferences');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setPreferences(initialPreferences || {});
    setIsEditing(false);
    setError(null);
  };

  const handleEmailToggle = (enabled) => {
    setPreferences(prev => ({
      ...prev,
      email: {
        ...prev.email,
        enabled
      }
    }));
  };

  const handleAlarmType = (alarmType, enabled) => {
    setPreferences(prev => ({
      ...prev,
      alarmTypes: {
        ...prev.alarmTypes,
        [alarmType]: enabled
      }
    }));
  };

  const handlePhoneToggle = (enabled) => {
    setPreferences(prev => ({
      ...prev,
      phone: {
        ...prev.phone,
        enabled
      }
    }));
  };

  const addPhoneNumber = () => {
    if (!newPhoneNumber) return;
    
    const phoneNumbers = preferences.phone?.phoneNumbers || [];
    const exists = phoneNumbers.some(p => p.number === newPhoneNumber);
    
    if (exists) {
      setError('Phone number already exists');
      return;
    }

    setPreferences(prev => ({
      ...prev,
      phone: {
        ...prev.phone,
        phoneNumbers: [
          ...phoneNumbers,
          {
            number: newPhoneNumber,
            type: 'sms',
            isPrimary: phoneNumbers.length === 0
          }
        ]
      }
    }));

    setNewPhoneNumber('');
  };

  const removePhoneNumber = (index) => {
    const phoneNumbers = [...(preferences.phone?.phoneNumbers || [])];
    const wasPrimary = phoneNumbers[index].isPrimary;
    phoneNumbers.splice(index, 1);
    
    // If we removed the primary, make the first one primary
    if (wasPrimary && phoneNumbers.length > 0) {
      phoneNumbers[0].isPrimary = true;
    }
    
    setPreferences(prev => ({
      ...prev,
      phone: {
        ...prev.phone,
        phoneNumbers
      }
    }));
  };

  const setPrimaryPhone = (index) => {
    const phoneNumbers = [...(preferences.phone?.phoneNumbers || [])];
    phoneNumbers.forEach((p, i) => {
      p.isPrimary = i === index;
    });
    
    setPreferences(prev => ({
      ...prev,
      phone: {
        ...prev.phone,
        phoneNumbers
      }
    }));
  };

  const handlePushToggle = (enabled) => {
    setPreferences(prev => ({
      ...prev,
      push: {
        ...prev.push,
        enabled
      }
    }));
  };

  const alarmTypes = [
    { key: 'critical', label: 'Critical Alarms' },
    { key: 'warnings', label: 'Warnings' },
    { key: 'deviceOffline', label: 'Device Offline' }
  ];

  return (
    <div className="space-y-6">
      {/* Header with Edit/Save buttons */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Notification Preferences</h3>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            Edit Preferences
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Alert Types - one shared list for every channel */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <BellIcon className="w-5 h-5 text-gray-600" />
          <div>
            <h4 className="text-md font-medium text-gray-900">Alert Types</h4>
            <p className="text-sm text-gray-500">What this user is notified about on every enabled channel</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {alarmTypes.map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span className="text-sm text-gray-700">{label}</span>
              {isEditing ? (
                <Switch
                  checked={preferences.alarmTypes?.[key] !== false}
                  onChange={(checked) => handleAlarmType(key, checked)}
                  className=""
                >
                  <span className="sr-only">Enable {label}</span>
                </Switch>
              ) : (
                preferences.alarmTypes?.[key] !== false ? (
                  <CheckIcon className="w-4 h-4 text-green-500" />
                ) : (
                  <XMarkIcon className="w-4 h-4 text-gray-400" />
                )
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Email Notifications */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <EnvelopeIcon className="w-5 h-5 text-gray-600" />
            <h4 className="text-md font-medium text-gray-900">Email Notifications</h4>
          </div>
          {isEditing ? (
            <Switch
              checked={preferences.email?.enabled || false}
              onChange={handleEmailToggle}
              className=""
            >
              <span className="sr-only">Enable email notifications</span>
            </Switch>
          ) : (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              preferences.email?.enabled 
                ? 'bg-green-100 text-green-700' 
                : 'bg-gray-200 text-gray-600'
            }`}>
              {preferences.email?.enabled ? 'Enabled' : 'Disabled'}
            </span>
          )}
        </div>

      </div>

      {/* Phone/SMS Notifications */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <PhoneIcon className="w-5 h-5 text-gray-600" />
            <h4 className="text-md font-medium text-gray-900">Phone/SMS Notifications</h4>
          </div>
          {isEditing ? (
            <Switch
              checked={preferences.phone?.enabled || false}
              onChange={handlePhoneToggle}
              className=""
            >
              <span className="sr-only">Enable phone notifications</span>
            </Switch>
          ) : (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              preferences.phone?.enabled 
                ? 'bg-green-100 text-green-700' 
                : 'bg-gray-200 text-gray-600'
            }`}>
              {preferences.phone?.enabled ? 'Enabled' : 'Disabled'}
            </span>
          )}
        </div>

        {preferences.phone?.enabled && (
          <div className="space-y-4">
            {/* Phone Numbers List */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Numbers
              </label>
              <div className="space-y-2">
                {preferences.phone?.phoneNumbers?.map((phone, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm">{phone.number}</span>
                      {phone.isPrimary && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Primary</span>
                      )}
                    </div>
                    {isEditing && (
                      <div className="flex items-center gap-2">
                        {!phone.isPrimary && (
                          <button
                            onClick={() => setPrimaryPhone(index)}
                            className="text-xs text-blue-600 hover:text-blue-700"
                          >
                            Set Primary
                          </button>
                        )}
                        <button
                          onClick={() => removePhoneNumber(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Add New Phone Number */}
                {isEditing && (
                  <div className="flex items-center gap-2 mt-3">
                    <input
                      type="tel"
                      value={newPhoneNumber}
                      onChange={(e) => setNewPhoneNumber(e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      onClick={addPhoneNumber}
                      className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      <PlusIcon className="w-5 h-5" />
                    </button>
                  </div>
                )}
                
                {(!preferences.phone?.phoneNumbers || preferences.phone.phoneNumbers.length === 0) && !isEditing && (
                  <p className="text-sm text-gray-500 italic">No phone numbers configured</p>
                )}
              </div>
            </div>

          </div>
        )}
      </div>

      {/* Push Notifications */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <DevicePhoneMobileIcon className="w-5 h-5 text-gray-600" />
            <h4 className="text-md font-medium text-gray-900">Push Notifications</h4>
          </div>
          {isEditing ? (
            <Switch
              checked={preferences.push?.enabled || false}
              onChange={handlePushToggle}
              className=""
            >
              <span className="sr-only">Enable push notifications</span>
            </Switch>
          ) : (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              preferences.push?.enabled 
                ? 'bg-green-100 text-green-700' 
                : 'bg-gray-200 text-gray-600'
            }`}>
              {preferences.push?.enabled ? 'Enabled' : 'Disabled'}
            </span>
          )}
        </div>

        {preferences.push?.deviceTokens?.length > 0 && (
          <p className="text-sm text-gray-600">
            {preferences.push.deviceTokens.length} device(s) registered for push notifications
          </p>
        )}
      </div>
    </div>
  );
};

export default NotificationPreferencesEditor;